/**
 * Document Templates / E-Signature Templates Seed Data
 *
 * Defines seed data for:
 * - Document templates (e-signature templates synced from DocuSeal)
 * - Template categories
 * - Signing envelopes (sample completed/pending signatures)
 *
 * Templates are used for:
 * - Partner referral agreements
 * - NDAs and confidentiality agreements
 * - Vendor contracts
 * - Compliance disclosures
 * - Employment documents
 */

import { seedId } from '../utils';
import { CONSUMER_IDS } from './consumers';
import { BUSINESS_IDS } from './tenants';
import { USER_IDS } from './users';

// ============================================================================
// IDs
// ============================================================================

export const DOCUMENT_TEMPLATE_IDS = {
  // OpsMatrix Capital templates
  PARTNER_REFERRAL_AGREEMENT: seedId('doc-template-partner-referral', 1),
  BROKER_NDA: seedId('doc-template-broker-nda', 2),
  VENDOR_MASTER_AGREEMENT: seedId('doc-template-vendor-master', 3),
  BORROWER_DISCLOSURE: seedId('doc-template-borrower-disclosure', 4),
  PRIVACY_NOTICE: seedId('doc-template-privacy-notice', 5),
  LOAN_COMMITMENT_LETTER: seedId('doc-template-loan-commitment', 6),

  // 1Sharpe Capital templates
  SHARPE_PARTNER_AGREEMENT: seedId('doc-template-sharpe-partner', 7),
  SHARPE_NDA: seedId('doc-template-sharpe-nda', 8),
  SHARPE_DISCLOSURE: seedId('doc-template-sharpe-disclosure', 9),

  // Pacific Bridge Fund templates
  PACIFIC_REFERRAL_AGREEMENT: seedId('doc-template-pacific-referral', 10),
} as const;

export const TEMPLATE_CATEGORY_IDS = {
  PARTNERSHIP: seedId('template-cat-partnership', 1),
  NDA: seedId('template-cat-nda', 2),
  DISCLOSURE: seedId('template-cat-disclosure', 3),
  VENDOR: seedId('template-cat-vendor', 4),
  LOAN_DOCS: seedId('template-cat-loan-docs', 5),
  EMPLOYMENT: seedId('template-cat-employment', 6),
} as const;

export const SIGNING_ENVELOPE_IDS = {
  // Completed signings
  MARCUS_PARTNER_AGREEMENT: seedId('envelope-marcus-partner', 1),
  SARAH_NDA: seedId('envelope-sarah-nda', 2),

  // Pending signings
  ROBERT_DISCLOSURE: seedId('envelope-robert-disclosure', 3),
  JESSICA_PARTNER_PENDING: seedId('envelope-jessica-partner', 4),
} as const;

// ============================================================================
// Template Categories
// ============================================================================

/**
 * Issue #1561 — Platform-default template-category seed.
 *
 * Per CLAUDE.md §2f: platform-default surfaces live in their own template
 * table (`template_category_templates`) with no `businessId` column.
 * Per-tenant overrides are forks (`TemplateCategory.templateId` set).
 */
export interface TemplateCategoryTemplateSeedData {
  id: string;
  name: string;
  label: string;
  description: string;
  icon: string;
  color: string;
  sortOrder: number;
  isActive: boolean;
}

/** @deprecated Use TemplateCategoryTemplateSeedData (#1561). Kept as an alias to avoid breaking callers mid-migration. */
export type TemplateCategorySeedData = TemplateCategoryTemplateSeedData;

const TEMPLATE_CATEGORY_TEMPLATES: TemplateCategoryTemplateSeedData[] = [
  {
    id: TEMPLATE_CATEGORY_IDS.PARTNERSHIP,
    name: 'partnership',
    label: 'Partnership Agreements',
    description: 'Referral partner and broker agreements',
    icon: 'handshake',
    color: '#4CAF50',
    sortOrder: 1,
    isActive: true,
  },
  {
    id: TEMPLATE_CATEGORY_IDS.NDA,
    name: 'nda',
    label: 'Non-Disclosure Agreements',
    description: 'Confidentiality and NDA documents',
    icon: 'lock',
    color: '#2196F3',
    sortOrder: 2,
    isActive: true,
  },
  {
    id: TEMPLATE_CATEGORY_IDS.DISCLOSURE,
    name: 'disclosure',
    label: 'Disclosures',
    description: 'Borrower and compliance disclosures',
    icon: 'description',
    color: '#FF9800',
    sortOrder: 3,
    isActive: true,
  },
  {
    id: TEMPLATE_CATEGORY_IDS.VENDOR,
    name: 'vendor',
    label: 'Vendor Contracts',
    description: 'Third-party vendor agreements',
    icon: 'business',
    color: '#9C27B0',
    sortOrder: 4,
    isActive: true,
  },
  {
    id: TEMPLATE_CATEGORY_IDS.LOAN_DOCS,
    name: 'loan_docs',
    label: 'Loan Documents',
    description: 'Loan commitment letters and term sheets',
    icon: 'account_balance',
    color: '#607D8B',
    sortOrder: 5,
    isActive: true,
  },
  {
    id: TEMPLATE_CATEGORY_IDS.EMPLOYMENT,
    name: 'employment',
    label: 'Employment',
    description: 'Employment agreements and onboarding',
    icon: 'badge',
    color: '#795548',
    sortOrder: 6,
    isActive: true,
  },
];

/** @deprecated Use TEMPLATE_CATEGORY_TEMPLATES (#1561). Kept as an alias. */
const TEMPLATE_CATEGORIES = TEMPLATE_CATEGORY_TEMPLATES;

// ============================================================================
// Document Templates
// ============================================================================

export interface DocumentTemplateSeedData {
  id: string;
  businessId: string;
  externalId: string; // DocuSeal template ID
  name: string;
  description: string;
  category: string;
  subcategory: string | null;
  fieldCount: number;
  fields: Array<{
    name: string;
    type: 'text' | 'signature' | 'date' | 'checkbox' | 'initials';
    required: boolean;
  }>;
  previewUrl: string | null;
  usageCount: number;
  lastUsedAt: Date | null;
  lastSyncedAt: Date;
  syncStatus: 'synced' | 'pending' | 'error';
  isActive: boolean;
  isDefault: boolean;
}

const DOCUMENT_TEMPLATES: DocumentTemplateSeedData[] = [
  // OpsMatrix Capital Templates
  {
    id: DOCUMENT_TEMPLATE_IDS.PARTNER_REFERRAL_AGREEMENT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    externalId: '1001',
    name: 'Partner Referral Agreement',
    description: 'Standard referral partner agreement with commission structure and terms',
    category: 'partnership',
    subcategory: 'referral',
    fieldCount: 12,
    fields: [
      { name: 'partner_name', type: 'text', required: true },
      { name: 'partner_company', type: 'text', required: true },
      { name: 'partner_email', type: 'text', required: true },
      { name: 'commission_rate', type: 'text', required: true },
      { name: 'effective_date', type: 'date', required: true },
      { name: 'territory', type: 'text', required: false },
      { name: 'exclusivity', type: 'checkbox', required: true },
      { name: 'non_compete', type: 'checkbox', required: true },
      { name: 'partner_signature', type: 'signature', required: true },
      { name: 'partner_initials', type: 'initials', required: true },
      { name: 'company_signature', type: 'signature', required: true },
      { name: 'signing_date', type: 'date', required: true },
    ],
    previewUrl: '/templates/previews/partner-referral-agreement.png',
    usageCount: 47,
    lastUsedAt: new Date('2026-01-15'),
    lastSyncedAt: new Date('2026-01-20'),
    syncStatus: 'synced',
    isActive: true,
    isDefault: true,
  },
  {
    id: DOCUMENT_TEMPLATE_IDS.BROKER_NDA,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    externalId: '1002',
    name: 'Broker NDA',
    description: 'Non-disclosure agreement for mortgage brokers and lending partners',
    category: 'nda',
    subcategory: 'broker',
    fieldCount: 8,
    fields: [
      { name: 'broker_name', type: 'text', required: true },
      { name: 'broker_company', type: 'text', required: true },
      { name: 'effective_date', type: 'date', required: true },
      { name: 'term_years', type: 'text', required: true },
      { name: 'broker_signature', type: 'signature', required: true },
      { name: 'broker_initials', type: 'initials', required: true },
      { name: 'witness_signature', type: 'signature', required: false },
      { name: 'signing_date', type: 'date', required: true },
    ],
    previewUrl: '/templates/previews/broker-nda.png',
    usageCount: 23,
    lastUsedAt: new Date('2026-01-10'),
    lastSyncedAt: new Date('2026-01-20'),
    syncStatus: 'synced',
    isActive: true,
    isDefault: true,
  },
  {
    id: DOCUMENT_TEMPLATE_IDS.VENDOR_MASTER_AGREEMENT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    externalId: '1003',
    name: 'Vendor Master Services Agreement',
    description: 'Master agreement for third-party service providers',
    category: 'vendor',
    subcategory: 'services',
    fieldCount: 15,
    fields: [
      { name: 'vendor_name', type: 'text', required: true },
      { name: 'vendor_address', type: 'text', required: true },
      { name: 'services_description', type: 'text', required: true },
      { name: 'contract_value', type: 'text', required: true },
      { name: 'payment_terms', type: 'text', required: true },
      { name: 'start_date', type: 'date', required: true },
      { name: 'end_date', type: 'date', required: true },
      { name: 'insurance_required', type: 'checkbox', required: true },
      { name: 'data_processing', type: 'checkbox', required: true },
      { name: 'vendor_signature', type: 'signature', required: true },
      { name: 'vendor_title', type: 'text', required: true },
      { name: 'vendor_initials', type: 'initials', required: true },
      { name: 'company_signature', type: 'signature', required: true },
      { name: 'company_title', type: 'text', required: true },
      { name: 'signing_date', type: 'date', required: true },
    ],
    previewUrl: '/templates/previews/vendor-msa.png',
    usageCount: 8,
    lastUsedAt: new Date('2026-01-05'),
    lastSyncedAt: new Date('2026-01-20'),
    syncStatus: 'synced',
    isActive: true,
    isDefault: true,
  },
  {
    id: DOCUMENT_TEMPLATE_IDS.BORROWER_DISCLOSURE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    externalId: '1004',
    name: 'Borrower Disclosure Package',
    description: 'Required disclosures for loan applicants including fees and terms',
    category: 'disclosure',
    subcategory: 'borrower',
    fieldCount: 10,
    fields: [
      { name: 'borrower_name', type: 'text', required: true },
      { name: 'property_address', type: 'text', required: true },
      { name: 'loan_amount', type: 'text', required: true },
      { name: 'interest_rate', type: 'text', required: true },
      { name: 'apr', type: 'text', required: true },
      { name: 'origination_fee', type: 'text', required: true },
      { name: 'borrower_signature', type: 'signature', required: true },
      { name: 'borrower_initials_1', type: 'initials', required: true },
      { name: 'borrower_initials_2', type: 'initials', required: true },
      { name: 'disclosure_date', type: 'date', required: true },
    ],
    previewUrl: '/templates/previews/borrower-disclosure.png',
    usageCount: 156,
    lastUsedAt: new Date('2026-01-18'),
    lastSyncedAt: new Date('2026-01-20'),
    syncStatus: 'synced',
    isActive: true,
    isDefault: true,
  },
  {
    id: DOCUMENT_TEMPLATE_IDS.PRIVACY_NOTICE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    externalId: '1005',
    name: 'Privacy Notice',
    description: 'GLBA and CCPA compliant privacy notice',
    category: 'disclosure',
    subcategory: 'privacy',
    fieldCount: 4,
    fields: [
      { name: 'recipient_name', type: 'text', required: true },
      { name: 'acknowledgment', type: 'checkbox', required: true },
      { name: 'recipient_signature', type: 'signature', required: true },
      { name: 'acknowledgment_date', type: 'date', required: true },
    ],
    previewUrl: '/templates/previews/privacy-notice.png',
    usageCount: 198,
    lastUsedAt: new Date('2026-01-18'),
    lastSyncedAt: new Date('2026-01-20'),
    syncStatus: 'synced',
    isActive: true,
    isDefault: false,
  },
  {
    id: DOCUMENT_TEMPLATE_IDS.LOAN_COMMITMENT_LETTER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    externalId: '1006',
    name: 'Loan Commitment Letter',
    description: 'Formal loan commitment with terms and conditions',
    category: 'loan_docs',
    subcategory: 'commitment',
    fieldCount: 14,
    fields: [
      { name: 'borrower_name', type: 'text', required: true },
      { name: 'borrower_entity', type: 'text', required: false },
      { name: 'property_address', type: 'text', required: true },
      { name: 'loan_amount', type: 'text', required: true },
      { name: 'interest_rate', type: 'text', required: true },
      { name: 'loan_term', type: 'text', required: true },
      { name: 'origination_points', type: 'text', required: true },
      { name: 'commitment_date', type: 'date', required: true },
      { name: 'expiration_date', type: 'date', required: true },
      { name: 'conditions', type: 'text', required: true },
      { name: 'borrower_signature', type: 'signature', required: true },
      { name: 'borrower_initials', type: 'initials', required: true },
      { name: 'lender_signature', type: 'signature', required: true },
      { name: 'signing_date', type: 'date', required: true },
    ],
    previewUrl: '/templates/previews/loan-commitment.png',
    usageCount: 89,
    lastUsedAt: new Date('2026-01-17'),
    lastSyncedAt: new Date('2026-01-20'),
    syncStatus: 'synced',
    isActive: true,
    isDefault: true,
  },

  // 1Sharpe Capital Templates
  {
    id: DOCUMENT_TEMPLATE_IDS.SHARPE_PARTNER_AGREEMENT,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    externalId: '2001',
    name: '1Sharpe Partner Agreement',
    description: 'Customized partner agreement for 1Sharpe Capital referral network',
    category: 'partnership',
    subcategory: 'referral',
    fieldCount: 11,
    fields: [
      { name: 'partner_name', type: 'text', required: true },
      { name: 'partner_company', type: 'text', required: true },
      { name: 'license_number', type: 'text', required: true },
      { name: 'commission_tier', type: 'text', required: true },
      { name: 'effective_date', type: 'date', required: true },
      { name: 'states_licensed', type: 'text', required: true },
      { name: 'exclusivity', type: 'checkbox', required: true },
      { name: 'partner_signature', type: 'signature', required: true },
      { name: 'partner_initials', type: 'initials', required: true },
      { name: 'company_signature', type: 'signature', required: true },
      { name: 'signing_date', type: 'date', required: true },
    ],
    previewUrl: '/templates/previews/sharpe-partner-agreement.png',
    usageCount: 34,
    lastUsedAt: new Date('2026-01-12'),
    lastSyncedAt: new Date('2026-01-20'),
    syncStatus: 'synced',
    isActive: true,
    isDefault: true,
  },
  {
    id: DOCUMENT_TEMPLATE_IDS.SHARPE_NDA,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    externalId: '2002',
    name: '1Sharpe Confidentiality Agreement',
    description: 'NDA for 1Sharpe Capital partners and vendors',
    category: 'nda',
    subcategory: null,
    fieldCount: 6,
    fields: [
      { name: 'party_name', type: 'text', required: true },
      { name: 'party_company', type: 'text', required: true },
      { name: 'effective_date', type: 'date', required: true },
      { name: 'party_signature', type: 'signature', required: true },
      { name: 'witness_signature', type: 'signature', required: false },
      { name: 'signing_date', type: 'date', required: true },
    ],
    previewUrl: '/templates/previews/sharpe-nda.png',
    usageCount: 18,
    lastUsedAt: new Date('2026-01-08'),
    lastSyncedAt: new Date('2026-01-20'),
    syncStatus: 'synced',
    isActive: true,
    isDefault: true,
  },
  {
    id: DOCUMENT_TEMPLATE_IDS.SHARPE_DISCLOSURE,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    externalId: '2003',
    name: '1Sharpe Borrower Disclosures',
    description: 'Complete disclosure package for 1Sharpe borrowers',
    category: 'disclosure',
    subcategory: 'borrower',
    fieldCount: 8,
    fields: [
      { name: 'borrower_name', type: 'text', required: true },
      { name: 'property_address', type: 'text', required: true },
      { name: 'loan_program', type: 'text', required: true },
      { name: 'acknowledgment', type: 'checkbox', required: true },
      { name: 'borrower_signature', type: 'signature', required: true },
      { name: 'borrower_initials', type: 'initials', required: true },
      { name: 'co_borrower_signature', type: 'signature', required: false },
      { name: 'disclosure_date', type: 'date', required: true },
    ],
    previewUrl: '/templates/previews/sharpe-disclosure.png',
    usageCount: 67,
    lastUsedAt: new Date('2026-01-16'),
    lastSyncedAt: new Date('2026-01-20'),
    syncStatus: 'synced',
    isActive: true,
    isDefault: true,
  },

  // Pacific Bridge Fund Templates
  {
    id: DOCUMENT_TEMPLATE_IDS.PACIFIC_REFERRAL_AGREEMENT,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    externalId: '3001',
    name: 'Pacific Bridge Referral Agreement',
    description: 'Referral partner agreement for Pacific Bridge Fund commercial lending',
    category: 'partnership',
    subcategory: 'referral',
    fieldCount: 13,
    fields: [
      { name: 'partner_name', type: 'text', required: true },
      { name: 'partner_company', type: 'text', required: true },
      { name: 'partner_address', type: 'text', required: true },
      { name: 'tax_id', type: 'text', required: true },
      { name: 'commission_structure', type: 'text', required: true },
      { name: 'minimum_loan_size', type: 'text', required: true },
      { name: 'effective_date', type: 'date', required: true },
      { name: 'geographic_focus', type: 'text', required: false },
      { name: 'property_types', type: 'text', required: true },
      { name: 'partner_signature', type: 'signature', required: true },
      { name: 'partner_initials', type: 'initials', required: true },
      { name: 'company_signature', type: 'signature', required: true },
      { name: 'signing_date', type: 'date', required: true },
    ],
    previewUrl: '/templates/previews/pacific-referral-agreement.png',
    usageCount: 12,
    lastUsedAt: new Date('2026-01-14'),
    lastSyncedAt: new Date('2026-01-20'),
    syncStatus: 'synced',
    isActive: true,
    isDefault: true,
  },
];

// ============================================================================
// Signing Envelopes
// ============================================================================

export interface SigningEnvelopeSeedData {
  id: string;
  businessId: string;
  templateId: string;
  docuSealTemplateId: number | null;
  agreementId: string | null;
  agreementType: string | null;
  docuSealSubmissionId: number | null;
  name: string;
  description: string | null;
  prefillValues: Record<string, string>;
  status: 'draft' | 'sent' | 'partially_signed' | 'completed' | 'voided' | 'expired';
  expiresAt: Date | null;
  sentAt: Date | null;
  completedAt: Date | null;
  voidedAt: Date | null;
  caseId: string | null;
  consumerId: string | null;
  createdBy: string;
  createdByEmail: string;
  createdByName: string;
}

const SIGNING_ENVELOPES: SigningEnvelopeSeedData[] = [
  // Completed partner agreement for Marcus
  {
    id: SIGNING_ENVELOPE_IDS.MARCUS_PARTNER_AGREEMENT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    templateId: DOCUMENT_TEMPLATE_IDS.PARTNER_REFERRAL_AGREEMENT,
    docuSealTemplateId: 1001,
    agreementId: null,
    agreementType: 'referral_agreement',
    docuSealSubmissionId: 50001,
    name: 'Partner Agreement - Marcus Thompson',
    description: 'Referral partner agreement for InvestorPro Capital',
    prefillValues: {
      partner_name: 'Marcus Thompson',
      partner_company: 'InvestorPro Capital',
      partner_email: 'marcus.thompson@investorpro.com',
      commission_rate: '1.25%',
      effective_date: '2025-06-15',
      territory: 'California, Nevada, Arizona',
    },
    status: 'completed',
    expiresAt: null,
    sentAt: new Date('2025-06-10'),
    completedAt: new Date('2025-06-15'),
    voidedAt: null,
    caseId: null,
    consumerId: CONSUMER_IDS.MARCUS_THOMPSON,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
    createdByEmail: 'rachel.martinez@opsmatrix.local',
    createdByName: 'Rachel Martinez',
  },
  // Completed NDA for Sarah
  {
    id: SIGNING_ENVELOPE_IDS.SARAH_NDA,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    templateId: DOCUMENT_TEMPLATE_IDS.BROKER_NDA,
    docuSealTemplateId: 1002,
    agreementId: null,
    agreementType: 'nda',
    docuSealSubmissionId: 50002,
    name: 'Broker NDA - Sarah Chen',
    description: 'Non-disclosure agreement for broker onboarding',
    prefillValues: {
      broker_name: 'Sarah Chen',
      broker_company: 'Chen Commercial Realty',
      effective_date: '2025-08-01',
      term_years: '3',
    },
    status: 'completed',
    expiresAt: null,
    sentAt: new Date('2025-07-28'),
    completedAt: new Date('2025-08-01'),
    voidedAt: null,
    caseId: null,
    consumerId: null,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
    createdByEmail: 'rachel.martinez@opsmatrix.local',
    createdByName: 'Rachel Martinez',
  },
  // Pending disclosure for Robert
  {
    id: SIGNING_ENVELOPE_IDS.ROBERT_DISCLOSURE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    templateId: DOCUMENT_TEMPLATE_IDS.BORROWER_DISCLOSURE,
    docuSealTemplateId: 1004,
    agreementId: null,
    agreementType: 'disclosure',
    docuSealSubmissionId: 50003,
    name: 'Borrower Disclosures - Robert Kim',
    description: 'Required disclosures for bridge loan application',
    prefillValues: {
      borrower_name: 'Robert Kim',
      property_address: '789 Sunset Blvd, Los Angeles, CA 90028',
      loan_amount: '$2,800,000',
      interest_rate: '11.5%',
      apr: '12.85%',
      origination_fee: '2.0 points',
    },
    status: 'sent',
    expiresAt: new Date('2026-02-15'),
    sentAt: new Date('2026-01-20'),
    completedAt: null,
    voidedAt: null,
    caseId: null,
    consumerId: CONSUMER_IDS.ROBERT_CHEN,
    createdBy: USER_IDS.MIKE_CHEN,
    createdByEmail: 'michael.chen@opsmatrix.local',
    createdByName: 'Michael Chen',
  },
  // Pending partner agreement for Jessica (partially signed)
  {
    id: SIGNING_ENVELOPE_IDS.JESSICA_PARTNER_PENDING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    templateId: DOCUMENT_TEMPLATE_IDS.PARTNER_REFERRAL_AGREEMENT,
    docuSealTemplateId: 1001,
    agreementId: null,
    agreementType: 'referral_agreement',
    docuSealSubmissionId: 50004,
    name: 'Partner Agreement - Jessica Wong',
    description: 'New referral partner onboarding agreement',
    prefillValues: {
      partner_name: 'Jessica Wong',
      partner_company: 'Wong Holdings LLC',
      partner_email: 'jessica.wong@wongholdings.com',
      commission_rate: '1.0%',
      effective_date: '2026-02-01',
      territory: 'Northern California',
    },
    status: 'partially_signed',
    expiresAt: new Date('2026-02-10'),
    sentAt: new Date('2026-01-18'),
    completedAt: null,
    voidedAt: null,
    caseId: null,
    consumerId: CONSUMER_IDS.JESSICA_LEE,
    createdBy: USER_IDS.RACHEL_MARTINEZ,
    createdByEmail: 'rachel.martinez@opsmatrix.local',
    createdByName: 'Rachel Martinez',
  },
];

// ============================================================================
// Getter Functions
// ============================================================================

/**
 * Issue #1561 — Returns the platform-default category seed (formerly the
 * `businessId IS NULL` rows). Now writes to `template_category_templates`
 * (no businessId), per §2f.
 */
export function getAllTemplateCategoryTemplates(): TemplateCategoryTemplateSeedData[] {
  return TEMPLATE_CATEGORY_TEMPLATES;
}

/** @deprecated Use getAllTemplateCategoryTemplates (#1561). */
export function getAllTemplateCategories(): TemplateCategoryTemplateSeedData[] {
  return TEMPLATE_CATEGORY_TEMPLATES;
}

/** @deprecated Use getAllTemplateCategoryTemplates (#1561). All seed rows are platform-default by definition. */
export function getSystemTemplateCategories(): TemplateCategoryTemplateSeedData[] {
  return TEMPLATE_CATEGORY_TEMPLATES;
}

export function getAllDocumentTemplates(): DocumentTemplateSeedData[] {
  return DOCUMENT_TEMPLATES;
}

export function getDocumentTemplatesByBusiness(businessId: string): DocumentTemplateSeedData[] {
  return DOCUMENT_TEMPLATES.filter((t) => t.businessId === businessId);
}

export function getDocumentTemplatesByCategory(category: string): DocumentTemplateSeedData[] {
  return DOCUMENT_TEMPLATES.filter((t) => t.category === category);
}

export function getAllSigningEnvelopes(): SigningEnvelopeSeedData[] {
  return SIGNING_ENVELOPES;
}

export function getSigningEnvelopesByStatus(status: SigningEnvelopeSeedData['status']): SigningEnvelopeSeedData[] {
  return SIGNING_ENVELOPES.filter((e) => e.status === status);
}

export function getPendingSigningEnvelopes(): SigningEnvelopeSeedData[] {
  return SIGNING_ENVELOPES.filter((e) => ['sent', 'partially_signed'].includes(e.status));
}

export function getCompletedSigningEnvelopes(): SigningEnvelopeSeedData[] {
  return SIGNING_ENVELOPES.filter((e) => e.status === 'completed');
}
